/**
 * Отдельный бюджет на диагностические записи в клиентские логи.
 *
 * Лимит `/api/client_logs` — 300 записей в минуту на IP, и он общий для всей
 * офисной сети за одним NAT: пятнадцать участников из одного офиса делят его
 * между собой. Если статистика WebRTC съест этот лимит, в Loki не доедет ни
 * ошибка входа, ни ICE-событие — то есть ровно то, ради чего логи и собирают.
 *
 * Поэтому у диагностики своя квота: двадцатая часть общего лимита. Когда она
 * кончилась, снимки молча пропускаются до начала следующего окна.
 */

const WINDOW_MS = 60_000

/**
 * Предел диагностических записей за минуту.
 *
 * Шестьдесят записей — это примерно четыре трека с опросом раз в тридцать
 * секунд плюс стартовый всплеск, то есть втрое больше, чем даёт обычная встреча.
 */
const MAX_ENTRIES_PER_WINDOW = 60

let entries: number[] = []

/**
 * Выбрасывает отметки старше минуты, чтобы окно не росло бесконечно.
 *
 * @param now - текущее время в миллисекундах
 */
function pruneExpired(now: number): void {
  const threshold = now - WINDOW_MS

  if (entries.length === 0) {
    return
  }

  entries = entries.filter((timestamp) => timestamp > threshold)
}

/**
 * Проверяет, влезает ли диагностика в отведённую долю минутного лимита.
 *
 * При успехе запись сразу занимает место в окне, поэтому вызывать функцию
 * нужно только тогда, когда снимок действительно отправляется.
 *
 * @param count - сколько записей собираемся отправить, по умолчанию одна
 */
export function canLogDiagnostics(count = 1): boolean {
  const now = Date.now()

  pruneExpired(now)

  if (count <= 0 || count > MAX_ENTRIES_PER_WINDOW) {
    return false
  }

  if (entries.length + count > MAX_ENTRIES_PER_WINDOW) {
    return false
  }

  for (let index = 0; index < count; index += 1) {
    entries.push(now)
  }

  return true
}

/**
 * Возвращает, сколько диагностических записей ушло за последнюю минуту.
 *
 * Нужна для предупреждения в логе: по числу видно, что бюджет кончился,
 * и пропуск снимков в Loki объясним.
 */
export function getDiagnosticsBudgetUsage(): number {
  pruneExpired(Date.now())

  return entries.length
}
